import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, History, SkipForward, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Loading } from "@/components/common";
import { Action, Panel, PanelHeader, StatusBadge } from "./ui";
import { FlagChips, PatientAvatar, dateTimeBR, dayBR } from "./MeetingBits";
import { AllocationPanel } from "./MeetingAllocation";
import { ageLabel, wardLabel } from "@/lib/patient";
import { ConversationsMeetingCard } from "./Conversations";
import { AttendanceIndicator } from "./MeetingGrid";
import { readMeetingWard } from "./meetingFilters";
import { StaffPicker } from "./StaffPicker";

const TEXTAREA =
  "mt-2 w-full rounded-xl border border-border bg-white px-3.5 py-3 text-[14.5px] leading-relaxed text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15";

const ROLE_FIELDS = [
  { key: "counselor", field: "counselor_id", label: "Conselheiro" },
  { key: "psychologist", field: "psychologist_id", label: "Psicólogo(a)" },
  { key: "psychiatrist", field: "psychiatrist_id", label: "Psiquiatra" },
];

export default function MeetingDiscussion() {
  const { meetingId, patientId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [decisions, setDecisions] = useState("");
  const [keepConduct, setKeepConduct] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fullHistory, setFullHistory] = useState(false);
  const [options, setOptions] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [showAllocation, setShowAllocation] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [comment, setComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [dischargeDate, setDischargeDate] = useState("");
  const [savingDischarge, setSavingDischarge] = useState(false);
  const dirty = useRef(false);
  const loaded = useRef(false);

  const load = useCallback(
    async (full = false) => {
      try {
        const { data: detail } = await api.get(`/portal/meetings/${meetingId}/patients/${patientId}`, {
          params: { full },
        });
        setData(detail);
        setDischargeDate(String(detail.patient?.expected_discharge_date || "").slice(0, 10));
        if (!loaded.current) {
          setNotes(detail.discussion?.notes || "");
          setDecisions(detail.discussion?.decisions || "");
          setKeepConduct(Boolean(detail.discussion?.keep_current_conduct));
          loaded.current = true;
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meetingId, patientId]
  );

  useEffect(() => {
    load(fullHistory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, patientId, fullHistory]);

  useEffect(() => {
    api
      .get("/portal/meetings/filter-options")
      .then(({ data: o }) => setOptions(o))
      .catch(() => setOptions(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = useCallback(
    async (silent = true) => {
      if (!data?.editable) return;
      setSaving(true);
      try {
        await api.put(`/portal/meetings/${meetingId}/patients/${patientId}/draft`, {
          notes,
          decisions,
          keep_current_conduct: keepConduct,
        });
        setSavedAt(new Date().toISOString());
        dirty.current = false;
        if (!silent) toast.success("Rascunho salvo.");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
      setSaving(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meetingId, patientId, notes, decisions, keepConduct, data?.editable]
  );

  useEffect(() => {
    if (!loaded.current || !dirty.current) return;
    const timer = setTimeout(() => saveDraft(true), 1200);
    return () => clearTimeout(timer);
  }, [notes, decisions, keepConduct, saveDraft]);

  const markDirty = () => {
    dirty.current = true;
  };

  const conclude = async () => {
    await saveDraft(true);
    try {
      await api.post(`/portal/meetings/${meetingId}/patients/${patientId}/conclude`);
      toast.success("Discussão concluída.");
      navigate(`/portal/reunioes/${meetingId}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveDischarge = async () => {
    setSavingDischarge(true);
    try {
      await api.put(`/portal/meetings/${meetingId}/patients/${patientId}/expected-discharge`, {
        expected_discharge_date: dischargeDate || null,
      });
      toast.success("Data prevista de alta atualizada no cadastro do paciente.");
      await load(fullHistory);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSavingDischarge(false);
  };

  const addComment = async () => {
    if (!comment.trim()) {
      toast.error("Escreva o comentário antes de registrar.");
      return;
    }
    setPostingComment(true);
    try {
      await api.post(`/portal/meetings/${meetingId}/patients/${patientId}/comments`, { text: comment });
      toast.success("Comentário registrado.");
      setComment("");
      await load(fullHistory);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setPostingComment(false);
  };

  const toggleFlag = async (flag, action) => {
    try {
      await api.post(`/portal/meetings/${meetingId}/patients/${patientId}/flags`, { flag, action });
      toast.success(action === "open" ? "Marcação ativada." : "Marcação encerrada.");
      await load(fullHistory);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const applyReallocation = async () => {
    if (!pendingRole) return;
    try {
      await api.put(`/portal/meetings/${meetingId}/patients/${patientId}/care-team`, {
        [pendingRole.field]: pendingRole.newId,
      });
      toast.success("Profissional atualizado no cadastro do paciente.");
      setPendingRole(null);
      await load(fullHistory);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openAllocation = async () => {
    await saveDraft(true);
    try {
      const { data: alloc } = await api.get("/portal/meetings/allocation", {
        params: { ward: readMeetingWard(meetingId) },
      });
      setAllocation(alloc);
      setShowAllocation(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return <Loading label="Carregando discussão…" />;
  const p = data.patient;
  const activeFlags = (p.flags || []).map((f) => f.flag);
  const editable = data.editable;

  return (
    <div data-testid="meeting-discussion-page" className="space-y-6">
      <button
        type="button"
        data-testid="discussion-back-to-queue"
        onClick={() => navigate(`/portal/reunioes/${meetingId}`)}
        className="inline-flex items-center gap-2 text-[14px] font-semibold text-ink-600 hover:text-sage-700"
      >
        <ArrowLeft size={16} aria-hidden="true" /> Voltar para a fila
      </button>

      <Panel testId="discussion-header" className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex gap-4">
            <PatientAvatar
              name={p.full_name}
              initials={p.initials}
              photoUrl={p.photo_url || p.photo_thumb_url}
              size={64}
              testId="discussion-avatar"
            />
            <div className="space-y-1.5">
              <h1 data-testid="discussion-patient-name" className="font-display text-[24px] font-semibold text-ink-900">
                {p.full_name}
              </h1>
              <p className="text-[14px] text-ink-600">
                <span data-testid="discussion-patient-age">{ageLabel(p)}</span> · Ala: {wardLabel(p)}
              </p>
              <p className="text-[14px] text-ink-600">
                Internação {dayBR(p.admission_date)} · {p.admission_days ?? "—"} dia(s) internado · Alta prevista{" "}
                <span data-testid="discussion-expected-discharge">{dayBR(p.expected_discharge_date)}</span>
              </p>
              {editable ? (
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-600">
                    Data prevista de alta
                  </label>
                  <input
                    type="date"
                    data-testid="discussion-expected-discharge-input"
                    value={dischargeDate}
                    onChange={(e) => setDischargeDate(e.target.value)}
                    className="h-10 rounded-xl border border-border bg-white px-3 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
                  />
                  <Action
                    size="sm"
                    data-testid="discussion-expected-discharge-save"
                    disabled={savingDischarge}
                    onClick={saveDischarge}
                  >
                    {savingDischarge ? "Salvando…" : "Salvar alta prevista"}
                  </Action>
                </div>
              ) : null}
              <p className="text-[14px] text-ink-600">
                Última discussão: {p.last_discussion_at ? dateTimeBR(p.last_discussion_at) : "nunca"}
                {p.days_without_discussion != null ? ` · ${p.days_without_discussion} dia(s) sem discussão` : ""}
              </p>
              <p className="text-[14px] text-ink-600">
                Conselheiro: {p.care_team?.counselor?.name || "não definido"} · Psicólogo(a):{" "}
                {p.care_team?.psychologist?.name || "não definido"} · Psiquiatra:{" "}
                {p.care_team?.psychiatrist?.name || "não definido"}
              </p>
              <FlagChips flags={p.flags} testId="discussion-flags" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge tone={data.discussion?.status === "done" ? "done" : "pending"} testId="discussion-status">
              {data.discussion?.status === "done" ? "Discutido nesta reunião" : "Pendente nesta reunião"}
            </StatusBadge>
            <Action
              variant="secondary"
              size="sm"
              icon={Users}
              data-testid="discussion-allocation-button"
              onClick={openAllocation}
            >
              Ver alocação da equipe
            </Action>
          </div>
        </div>
      </Panel>

      {showAllocation && allocation ? (
        <AllocationPanel data={allocation} onClose={() => setShowAllocation(false)} backLabel="Ocultar" />
      ) : null}

      <AttendanceIndicator patientId={p.id} compact />

      <ConversationsMeetingCard patientId={p.id} patientName={p.full_name} />

      <Panel testId="discussion-record" className="space-y-5">
        <PanelHeader
          label="Discussão atual"
          title="Registro da equipe"
          hint={
            editable
              ? "As anotações são salvas automaticamente como rascunho."
              : "Reunião encerrada: o conteúdo original está bloqueado para edição."
          }
        />
        <div>
          <label className="block text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-600">
            Observações da discussão
          </label>
          <textarea
            data-testid="discussion-notes-input"
            rows={5}
            disabled={!editable}
            value={notes}
            onChange={(e) => {
              markDirty();
              setNotes(e.target.value);
            }}
            className={TEXTAREA}
            placeholder="Comentários, evolução e contexto discutidos pela equipe"
          />
        </div>
        <div>
          <label className="block text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-600">
            Definições e encaminhamentos
          </label>
          <textarea
            data-testid="discussion-decisions-input"
            rows={4}
            disabled={!editable}
            value={decisions}
            onChange={(e) => {
              markDirty();
              setDecisions(e.target.value);
            }}
            className={TEXTAREA}
            placeholder="O que foi definido para o paciente"
          />
        </div>
        <label className="flex items-center gap-2.5 text-[14.5px] text-ink-800">
          <input
            type="checkbox"
            data-testid="discussion-keep-conduct"
            disabled={!editable}
            checked={keepConduct}
            onChange={(e) => {
              markDirty();
              setKeepConduct(e.target.checked);
            }}
          />
          Mantida a conduta atual
        </label>
        <p data-testid="discussion-draft-status" className="text-[13px] text-ink-500">
          {saving ? "Salvando rascunho…" : savedAt ? `Rascunho salvo às ${dateTimeBR(savedAt)}` : "Nenhuma alteração pendente."}
        </p>
        {editable ? (
          <div className="flex flex-wrap gap-2.5">
            <Action icon={ArrowRight} data-testid="discussion-conclude" onClick={conclude}>
              Concluir e selecionar o próximo
            </Action>
            <Action
              variant="secondary"
              icon={ArrowLeft}
              data-testid="discussion-back-button"
              onClick={async () => {
                await saveDraft(true);
                navigate(`/portal/reunioes/${meetingId}`);
              }}
            >
              Voltar para a fila
            </Action>
            <Action
              variant="secondary"
              icon={SkipForward}
              data-testid="discussion-skip-button"
              onClick={async () => {
                await saveDraft(true);
                toast.success("Paciente segue pendente nesta reunião.");
                navigate(`/portal/reunioes/${meetingId}`);
              }}
            >
              Pular por agora
            </Action>
          </div>
        ) : null}
      </Panel>

      <Panel testId="discussion-comments-panel" className="space-y-4">
        <PanelHeader
          label="Comentários"
          title="Comentários desta reunião"
          hint="Cada comentário fica registrado com autor e horário e entra na ata desta reunião."
        />
        {(data.comments || []).length === 0 ? (
          <p data-testid="discussion-comments-empty" className="text-[14px] text-ink-500">
            Nenhum comentário registrado nesta reunião para este paciente.
          </p>
        ) : (
          <ul data-testid="discussion-comments-list" className="space-y-2">
            {data.comments.map((c) => (
              <li
                key={c.id}
                data-testid={`discussion-comment-${c.id}`}
                className="rounded-xl bg-sand/50 px-3.5 py-2.5 text-[14.5px] text-ink-800"
              >
                {c.text}
                <span className="mt-0.5 block text-[12.5px] text-ink-500">
                  {dateTimeBR(c.created_at)}
                  {c.author_name ? ` · ${c.author_name}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
        {editable ? (
          <div className="space-y-3">
            <textarea
              data-testid="discussion-comment-input"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className={TEXTAREA}
              placeholder="Escreva um comentário sobre este paciente"
            />
            <Action data-testid="discussion-comment-save" disabled={postingComment} onClick={addComment}>
              {postingComment ? "Registrando…" : "Registrar comentário"}
            </Action>
          </div>
        ) : null}
      </Panel>

      <Panel testId="discussion-flags-panel" className="space-y-4">
        <PanelHeader
          label="Marcações persistentes"
          title="Situações em acompanhamento"
          hint="As marcações continuam ativas entre reuniões até serem encerradas."
        />
        <div className="flex flex-wrap gap-2.5">
          {data.flag_catalog.map((f) => {
            const active = activeFlags.includes(f.flag);
            return (
              <Action
                key={f.flag}
                variant={active ? "primary" : "secondary"}
                size="sm"
                disabled={!editable}
                data-testid={`discussion-flag-${f.flag}`}
                onClick={() => toggleFlag(f.flag, active ? "close" : "open")}
              >
                {active ? `Encerrar: ${f.label}` : f.label}
              </Action>
            );
          })}
        </div>
        {data.flag_history?.length ? (
          <ul data-testid="discussion-flag-history" className="space-y-1.5 text-[13.5px] text-ink-600">
            {data.flag_history.map((h, i) => (
              <li key={`${h.flag}-${i}`} className="rounded-xl bg-sand/50 px-3.5 py-2.5">
                {h.label} — aberta em {dateTimeBR(h.opened_at)} por {h.opened_by_name || "—"}
                {h.status === "closed" ? ` · encerrada em ${dateTimeBR(h.closed_at)} por ${h.closed_by_name || "—"}` : " · ativa"}
              </li>
            ))}
          </ul>
        ) : null}
      </Panel>

      <Panel testId="discussion-realloc-panel" className="space-y-4">
        <PanelHeader
          label="Equipe responsável"
          title="Realocação de profissionais"
          hint="A alteração é aplicada imediatamente no cadastro do paciente e registrada no histórico."
        />
        <div className="grid gap-5 sm:grid-cols-3">
          {ROLE_FIELDS.map((r) => (
            <StaffPicker
              key={r.key}
              label={r.label}
              options={options?.[r.key]?.items || []}
              value={p.care_team?.[r.key]?.id || null}
              disabled={!editable}
              testId={`discussion-realloc-${r.key}`}
              onChange={(id) =>
                setPendingRole({
                  ...r,
                  newId: id,
                  currentName: p.care_team?.[r.key]?.name || "não definido",
                  newName: (options?.[r.key]?.items || []).find((o) => o.id === id)?.name || "não definido",
                })
              }
            />
          ))}
        </div>
        {pendingRole ? (
          <div
            data-testid="discussion-realloc-confirm"
            className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5"
          >
            <p className="text-[14px] text-ink-800">
              Confirmar alteração de <b>{pendingRole.label}</b>: {pendingRole.currentName} → {pendingRole.newName}
            </p>
            <div className="flex flex-wrap gap-2.5">
              <Action size="sm" icon={Check} data-testid="discussion-realloc-apply" onClick={applyReallocation}>
                Confirmar alteração
              </Action>
              <Action size="sm" variant="secondary" data-testid="discussion-realloc-cancel" onClick={() => setPendingRole(null)}>
                Cancelar
              </Action>
            </div>
          </div>
        ) : null}
      </Panel>

      <Panel testId="discussion-history-panel" className="space-y-4">
        <PanelHeader
          label="Histórico recente"
          title={fullHistory ? "Todas as discussões anteriores" : "Três últimas discussões"}
          actions={
            fullHistory ? null : (
              <Action
                variant="secondary"
                size="sm"
                icon={History}
                data-testid="discussion-history-full"
                onClick={() => setFullHistory(true)}
              >
                Ver histórico completo
              </Action>
            )
          }
        />
        {data.history.length === 0 ? (
          <p className="text-[14px] text-ink-500">Nenhuma discussão anterior registrada.</p>
        ) : (
          <ol data-testid="discussion-history-list" className="space-y-3">
            {data.history.map((h) => (
              <li key={h.id} className="rounded-xl bg-sand/50 px-4 py-3.5">
                <p className="text-[14px] font-semibold text-ink-800">
                  Reunião de {dateTimeBR(h.meeting_date)} · {h.author_name || "—"}
                </p>
                {h.notes ? <p className="mt-1.5 text-[13.5px] text-ink-700">Observações: {h.notes}</p> : null}
                <p className="mt-1 text-[13.5px] text-ink-700">
                  Definições: {h.decisions || (h.keep_current_conduct ? "Mantida a conduta atual" : "—")}
                </p>
                {h.flag_changes?.length ? (
                  <p className="mt-1 text-[13.5px] text-ink-600">
                    Marcações:{" "}
                    {h.flag_changes.map((c) => `${c.label} (${c.action === "open" ? "aberta" : "encerrada"})`).join(", ")}
                  </p>
                ) : null}
                {h.reallocations?.length ? (
                  <p className="mt-1 text-[13.5px] text-ink-600">
                    Realocações:{" "}
                    {h.reallocations
                      .map((r) => `${r.role_label}: ${r.previous_name || "não definido"} → ${r.new_name || "não definido"}`)
                      .join(" · ")}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Panel>
    </div>
  );
}
